import React, { useMemo, useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EmployeeImage } from '@/components/EmployeeImage';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  AlertCircle,
  Users,
  Clock,
  Target,
  MessageSquare,
  Download,
  FileWarning
} from 'lucide-react';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { usePermissions } from '@/contexts/PermissionContext';

import { missingTimesheetsApi, type MissingTimesheet as ApiMissingTimesheet, type MissingTimesheetSummary } from '@/services/missingTimesheetsApi';
import { formatLastReminded } from '@/utils/formatters';

// Missing Timesheet Data Type
interface MissingTimesheet {
  id: string;
  employeeId?: number;
  employeeName: string;
  employeeEmail: string;
  department: string;
  manager: string;
  weekEnding: string;
  status: 'Missing' | 'Draft' | 'Rejected';
  daysOverdue: number;
  severity: 'grace' | 'warning' | 'urgent' | 'critical';
  issues: {
    type: 'missing_days' | 'under_hours' | 'no_project' | 'unapproved' | 'rejected' | 'late_submission';
    description: string;
    severity: 'error' | 'warning' | 'info';
  }[];
  missingDays?: string[]; // e.g., ['Mon', 'Tue', 'Wed']
  expectedHours?: number;
  submittedHours?: number;
  lastActivity?: string; // Last time they accessed the timesheet
  lastReminded?: string; // Last time a reminder was sent
  photoUrl?: string; // Employee photo URL
}

// Mock data for missing timesheets
const mockMissingTimesheets: MissingTimesheet[] = [
  {
    id: '1',
    employeeName: 'John Smith',
    employeeEmail: 'john.smith@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 12,
    severity: 'critical',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '12 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '07/01/2026',
    lastReminded: '15/01/2026',
  },
  {
    id: '2',
    employeeName: 'Alice Doe',
    employeeEmail: 'alice.doe@company.com',
    department: 'Sales',
    manager: 'Mike Brown',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 8,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'Missing Mon, Tue, Wed', severity: 'error' },
      { type: 'under_hours', description: '16 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Mon', 'Tue', 'Wed'],
    expectedHours: 40,
    submittedHours: 16,
    lastActivity: '11/01/2026',
    lastReminded: '17/01/2026',
  },
  {
    id: '3',
    employeeName: 'Kevin Lee',
    employeeEmail: 'kevin.lee@company.com',
    department: 'HR',
    manager: 'Jane Davis',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 4,
    severity: 'warning',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '15/01/2026',
    lastReminded: '18/01/2026',
  },
  {
    id: '4',
    employeeName: 'Sarah Johnson',
    employeeEmail: 'sarah.j@company.com',
    department: 'Finance',
    manager: 'Lisa White',
    weekEnding: '12/01/2026',
    status: 'Missing',
    daysOverdue: 1,
    severity: 'grace',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'warning' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '18/01/2026',
  },
  {
    id: '5',
    employeeName: 'Robert Taylor',
    employeeEmail: 'robert.t@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 9,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '9 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '10/01/2026',
  },
  {
    id: '6',
    employeeName: 'Emily Brown',
    employeeEmail: 'emily.b@company.com',
    department: 'Sales',
    manager: 'Mike Brown',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 5,
    severity: 'warning',
    issues: [
      { type: 'missing_days', description: 'Missing Thu, Fri', severity: 'error' },
      { type: 'under_hours', description: '24 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Thu', 'Fri'],
    expectedHours: 40,
    submittedHours: 24,
    lastActivity: '14/01/2026',
  },
  {
    id: '7',
    employeeName: 'Daniel Anderson',
    employeeEmail: 'daniel.a@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 6,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'Missing Mon, Tue', severity: 'error' },
      { type: 'under_hours', description: '24 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Mon', 'Tue'],
    expectedHours: 40,
    submittedHours: 24,
    lastActivity: '13/01/2026',
  },
  {
    id: '8',
    employeeName: 'Lisa Thompson',
    employeeEmail: 'lisa.t@company.com',
    department: 'Marketing',
    manager: 'Tom Anderson',
    weekEnding: '12/01/2026',
    status: 'Missing',
    daysOverdue: 2,
    severity: 'grace',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'warning' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '17/01/2026',
  },
  {
    id: '9',
    employeeName: 'Maria Garcia',
    employeeEmail: 'maria.g@company.com',
    department: 'Engineering',
    manager: 'Sarah Wilson',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 11,
    severity: 'critical',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '11 days overdue', severity: 'error' },
      { type: 'rejected', description: 'Previous timesheet rejected', severity: 'warning' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '08/01/2026',
  },
  {
    id: '10',
    employeeName: 'James Wilson',
    employeeEmail: 'james.w@company.com',
    department: 'Operations',
    manager: 'Chris Martin',
    weekEnding: '19/01/2026',
    status: 'Draft',
    daysOverdue: 3,
    severity: 'warning',
    issues: [
      { type: 'missing_days', description: 'Missing Wed', severity: 'error' },
      { type: 'under_hours', description: '32 of 40 hours submitted', severity: 'warning' },
    ],
    missingDays: ['Wed'],
    expectedHours: 40,
    submittedHours: 32,
    lastActivity: '16/01/2026',
  },
  {
    id: '11',
    employeeName: 'Patricia Martinez',
    employeeEmail: 'patricia.m@company.com',
    department: 'Finance',
    manager: 'Lisa White',
    weekEnding: '19/01/2026',
    status: 'Missing',
    daysOverdue: 13,
    severity: 'critical',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '13 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '06/01/2026',
  },
  {
    id: '12',
    employeeName: 'Michael Davis',
    employeeEmail: 'michael.d@company.com',
    department: 'HR',
    manager: 'Jane Davis',
    weekEnding: '12/01/2026',
    status: 'Missing',
    daysOverdue: 7,
    severity: 'urgent',
    issues: [
      { type: 'missing_days', description: 'No timesheet submitted', severity: 'error' },
      { type: 'late_submission', description: '7 days overdue', severity: 'error' },
    ],
    expectedHours: 40,
    submittedHours: 0,
    lastActivity: '12/01/2026',
  },
];

// Helper function to generate a random photo URL for employees without avatars
const getDemoPhotoUrl = (name: string, id: string): string => {
  const seed = encodeURIComponent(name.replace(/\s+/g, '-'));
  return `https://picsum.photos/seed/${seed}-${id}/300/300`;
};

// Helper function to check if URL is an Evo avatar (not from dicebear or other non-Evo sources)
const isEvoAvatarUrl = (url: string): boolean => {
  const trimmedUrl = url.trim().toLowerCase();
  if (trimmedUrl.includes('dicebear')) {
    return false;
  }
  return true;
};

// Helper function to get photo URL - uses Evo avatar if available, otherwise random image
const getPhotoUrl = (name: string, id: string, photoUrl?: string | null): string => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return getDemoPhotoUrl(name, id);
};

// Returns the real Employee.PhotoUrl if available, undefined otherwise.
// Used in live mode so EmployeeImage falls back to initials instead of fake avatars.
const getLivePhotoUrl = (photoUrl?: string | null): string | undefined => {
  if (photoUrl && photoUrl.trim().length > 0 && isEvoAvatarUrl(photoUrl)) {
    return photoUrl;
  }
  return undefined;
};

// Helper function to get severity color
const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-error-500';
    case 'urgent':
      return 'bg-warning-500';
    case 'warning':
      return 'bg-warning-300';
    case 'grace':
      return 'bg-success-500';
    default:
      return 'bg-muted';
  }
};

// Helper function to get severity label
const getSeverityLabel = (severity: string) => {
  switch (severity) {
    case 'critical':
      return '🔴 Critical (11+ days)';
    case 'urgent':
      return '🟠 Urgent (6-10 days)';
    case 'warning':
      return '🟡 Warning (3-5 days)';
    case 'grace':
      return '🟢 Grace (0-2 days)';
    default:
      return severity;
  }
};

export const MissingTimesheetsReportPage: React.FC = () => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('reports-missing-timesheets');
  const { currentCompany } = useCompany();
  const { canViewReport, canExportReport, getReportViewAccessLevel } = usePermissions();
  
  // State for API data
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiMissingTimesheets, setApiMissingTimesheets] = useState<MissingTimesheet[]>([]);
  const [apiSummary, setApiSummary] = useState<MissingTimesheetSummary | null>(null);
  
  // State for selected rows
  const [selectedEmployees, setSelectedEmployees] = useState<MissingTimesheet[]>([]);
  const [clearSelection, setClearSelection] = useState(false);

  // Fetch data from API when in live mode
  useEffect(() => {
    if (screenMode === 'live') {
      const fetchMissingTimesheets = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
          const viewScope = getReportViewAccessLevel('missingTimesheets');
          const [timesheetsResponse, summaryResponse] = await Promise.all([
            missingTimesheetsApi.getMissingTimesheets({ pageSize: 100, companyId: currentCompany?.id, viewScope }),
            missingTimesheetsApi.getSummary({ companyId: currentCompany?.id, viewScope })
          ]);
          
          // Transform API data to match local interface
          const transformedData: MissingTimesheet[] = timesheetsResponse.data.map((item: ApiMissingTimesheet) => ({
            id: item.id,
            employeeId: item.employeeId,
            employeeName: item.employeeName,
            employeeEmail: item.employeeEmail,
            department: item.department,
            manager: item.managerName || 'Unknown',
            weekEnding: item.weekEnding,
            status: item.status,
            daysOverdue: item.daysOverdue,
            severity: item.severity,
            issues: [], // API doesn't return issues, we'll derive them
            missingDays: item.missingDays,
            expectedHours: item.expectedHours,
            submittedHours: item.submittedHours,
            lastActivity: item.lastActivity,
            lastReminded: item.lastReminded,
            photoUrl: getLivePhotoUrl(item.photoUrl),
          }));
          
          setApiMissingTimesheets(transformedData);
          setApiSummary(summaryResponse);
        } catch (err) {
          console.error('Error fetching missing timesheets:', err);
          setError(err instanceof Error ? err.message : 'Failed to load missing timesheets');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchMissingTimesheets();
    }
  }, [screenMode, currentCompany?.id, getReportViewAccessLevel]);

  // Listen for company-changed event to refresh data when the active company is switched.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;
        if (screenMode === 'live') {
          setIsLoading(true);
          setError(null);
          const viewScope = getReportViewAccessLevel('missingTimesheets');
          Promise.all([
            missingTimesheetsApi.getMissingTimesheets({ pageSize: 100, companyId: currentCompany?.id, viewScope }),
            missingTimesheetsApi.getSummary({ companyId: currentCompany?.id, viewScope })
          ]).then(([timesheetsResponse, summaryResponse]) => {
            if (signal?.aborted) return;
            const transformedData: MissingTimesheet[] = timesheetsResponse.data.map((item: ApiMissingTimesheet) => ({
              id: item.id,
              employeeId: item.employeeId,
              employeeName: item.employeeName,
              employeeEmail: item.employeeEmail,
              department: item.department,
              manager: item.managerName || 'Unknown',
              weekEnding: item.weekEnding,
              status: item.status,
              daysOverdue: item.daysOverdue,
              severity: item.severity,
              issues: [],
              missingDays: item.missingDays,
              expectedHours: item.expectedHours,
              submittedHours: item.submittedHours,
              lastActivity: item.lastActivity,
              lastReminded: item.lastReminded,
              photoUrl: getLivePhotoUrl(item.photoUrl),
            }));
            setApiMissingTimesheets(transformedData);
            setApiSummary(summaryResponse);
          }).catch((err) => {
            if (signal?.aborted) return;
            console.error('Error fetching missing timesheets:', err);
            setError(err instanceof Error ? err.message : 'Failed to load missing timesheets');
          }).finally(() => {
            setIsLoading(false);
          });
        }
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [screenMode, currentCompany?.id, getReportViewAccessLevel]);

  // Use API data when in live mode, mock data when in demo mode
  // For demo mode, add random photo URLs to mock data
  const missingTimesheets = useMemo(() => {
    if (screenMode === 'live') {
      return apiMissingTimesheets;
    }
    // Add photo URLs to mock data for demo mode
    return mockMissingTimesheets.map(item => ({
      ...item,
      photoUrl: getPhotoUrl(item.employeeName, item.id, item.photoUrl),
    }));
  }, [screenMode, apiMissingTimesheets]);

  // Backend already enforces scoping via viewScope/ManagerIds/DeptHeadDepartments,
  // so no additional client-side filtering is needed.

  // Handler functions
  const handleSendReminders = async () => {
    const targetRows = selectedEmployees.length > 0 ? selectedEmployees : missingTimesheets;
    const totalCount = targetRows.length;

    if (totalCount === 0) return;

    if (screenMode === 'live') {
      try {
        // Group rows by weekEnding so each API call uses the correct date
        const byWeekEnding = new Map<string, number[]>();
        for (const row of targetRows) {
          if (row.employeeId === undefined) continue;
          const we = row.weekEnding;
          if (!byWeekEnding.has(we)) byWeekEnding.set(we, []);
          byWeekEnding.get(we)!.push(row.employeeId);
        }

        // Send reminders per unique weekEnding, using date-only string to avoid timezone shift
        await Promise.all(
          Array.from(byWeekEnding.entries()).map(([we, empIds]) => {
            const dateOnly = we.includes('T') ? we.split('T')[0] : we;
            return missingTimesheetsApi.sendReminders(empIds, dateOnly);
          })
        );

        // Update local state so the UI reflects the new lastReminded immediately
        const now = new Date().toISOString();
        const targetIds = new Set(targetRows.map(r => r.id));
        setApiMissingTimesheets(prev =>
          prev.map(t =>
            targetIds.has(t.id) ? { ...t, lastReminded: now } : t
          )
        );
        toast.success(`Successfully sent feed notifications to ${totalCount} employee${totalCount > 1 ? 's' : ''} with missing timesheets.`, {
          description: 'Reminders have been sent via Evo Feeds',
        });
      } catch (err) {
        console.error('Error sending reminders:', err);
        toast.error('Failed to send reminders', {
          description: err instanceof Error ? err.message : 'An error occurred',
        });
      }
    } else {
      toast.success(`Successfully sent feed notifications to ${totalCount} employee${totalCount > 1 ? 's' : ''} with missing timesheets.`, {
        description: 'Reminders have been sent via Evo Feeds',
      });
    }

    // Clear selection after sending
    if (selectedEmployees.length > 0) {
      setSelectedEmployees([]);
      setClearSelection(true);
      setTimeout(() => setClearSelection(false), 100);
    }
  };

  const handleExport = () => {
    // Check if there's data to export
    if (!missingTimesheets || missingTimesheets.length === 0) {
      toast.error('No data to export', {
        description: 'There are no missing timesheets to export.',
      });
      return;
    }

    // Prepare data for export
    const exportData = missingTimesheets.map(timesheet => ({
      'Employee': timesheet.employeeName,
      'Status': timesheet.status === 'Missing' ? 'Missing' : timesheet.status === 'Draft' ? 'Draft' : 'Rejected',
      'Week Ending': new Date(timesheet.weekEnding).toLocaleDateString('en-GB'),
      'Expected Hours': timesheet.expectedHours,
      'Submitted Hours': timesheet.submittedHours,
      'Missing Days': timesheet.missingDays ? timesheet.missingDays.join(', ') : '',
      'Days Overdue': timesheet.daysOverdue,
      'Last Reminded': timesheet.lastReminded ? formatLastReminded(timesheet.lastReminded) : 'Never',
      'Department': timesheet.department,
      'Manager': timesheet.manager,
    }));

    // Build worksheet and auto-size columns
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const colWidths = Object.keys(exportData[0]).map(key => ({
      wch: Math.min(
        40,
        Math.max(
          key.length,
          ...exportData.map(row => String(row[key as keyof typeof row] ?? '').length)
        )
      ),
    }));
    worksheet['!cols'] = colWidths;

    // Create workbook and download
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missing Timesheets');
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `missing-timesheets-${timestamp}.xlsx`);

    toast.success('Report exported successfully', {
      description: `Downloaded missing-timesheets-${timestamp}.xlsx`,
    });
  };

  // Calculate summary metrics
  const metrics = useMemo(() => {
    // Use API summary when in live mode and available
    if (screenMode === 'live' && apiSummary) {
      return {
        totalMissing: apiSummary.totalMissing,
        uniqueEmployees: apiSummary.uniqueEmployees,
        complianceRate: apiSummary.complianceRate.toFixed(1),
        avgDaysOverdue: apiSummary.avgDaysOverdue.toFixed(1),
        repeatOffenders: apiSummary.repeatOffenders,
        // Comparison values from API
        totalMissingChange: apiSummary.totalMissingChange ?? 0,
        complianceRateChange: apiSummary.complianceRateChange ?? 0,
        avgDaysOverdueChange: apiSummary.avgDaysOverdueChange ?? 0,
        repeatOffendersChange: apiSummary.repeatOffendersChange ?? 0,
      };
    }
    
    // Fall back to calculating from local data
    const total = missingTimesheets.length;
    const uniqueEmployees = new Set(missingTimesheets.map(t => t.employeeName)).size;
    const totalTimesheetsExpected = 500; // Mock total timesheets due
    const complianceRate = ((totalTimesheetsExpected - total) / totalTimesheetsExpected) * 100;
    const avgDaysOverdue = total > 0 ? missingTimesheets.reduce((sum, t) => sum + t.daysOverdue, 0) / total : 0;
    // Repeat offenders: employees with critical severity (11+ days overdue)
    const repeatOffenders = missingTimesheets.filter(t => t.severity === 'critical').length;

    return {
      totalMissing: total,
      uniqueEmployees,
      complianceRate: complianceRate.toFixed(1),
      avgDaysOverdue: avgDaysOverdue.toFixed(1),
      repeatOffenders,
      // Mock comparison values for demo mode
      totalMissingChange: -12,
      complianceRateChange: 2.1,
      avgDaysOverdueChange: -0.8,
      repeatOffendersChange: -3,
    };
  }, [screenMode, apiSummary, missingTimesheets]);

  // Define columns for DataTableEnhanced
  const columns: ColumnDef<MissingTimesheet>[] = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <EmployeeImage
            name={row.original.employeeName}
            photoUrl={row.original.photoUrl}
            size="sm"
          />
          <div className="font-medium">{row.original.employeeName}</div>
        </div>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'department',
      header: 'Department',
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'manager',
      header: 'Manager',
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'weekEnding',
      header: 'Week Ending',
      cell: ({ row }) => {
        const raw = row.original.weekEnding;
        try {
          const date = typeof raw === 'string' && raw.includes('T') ? parseISO(raw) : new Date(raw);
          return format(date, 'dd/MM/yyyy');
        } catch {
          return raw;
        }
      },
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'Missing' ? 'destructive' : 'secondary'}>
          {row.original.status}
        </Badge>
      ),
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'expectedHours',
      header: 'Expected Hours',
      cell: ({ row }) => {
        const submittedHours = row.original.submittedHours || 0;
        const expectedHours = row.original.expectedHours || 40;
        const missingDays = row.original.missingDays || [];

        return (
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {submittedHours} / {expectedHours} hours
            </div>
            {missingDays.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Missing: {missingDays.join(', ')}
              </div>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableGrouping: false,
    },
    {
      accessorKey: 'daysOverdue',
      header: 'Days Overdue',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.daysOverdue}</span>
      ),
      enableSorting: true,
      aggregationFn: 'mean',
      aggregatedCell: ({ getValue }) => `Avg: ${(getValue() as number).toFixed(1)}`,
    },
    {
      accessorKey: 'lastReminded',
      header: 'Last Reminded',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.lastReminded ? formatLastReminded(row.original.lastReminded) : 'Never'}
        </span>
      ),
      enableSorting: true,
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {}}
            title="Send Evo Feed notification"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ], []);

  // Field labels for better UX
  const fieldLabels: Record<string, string> = {
    severity: 'Severity',
    employeeName: 'Employee',
    department: 'Department',
    manager: 'Manager',
    weekEnding: 'Week Ending',
    status: 'Status',
    expectedHours: 'Expected Hours',
    daysOverdue: 'Days Overdue',
    lastReminded: 'Last Reminded',
  };

  // Field categories for organization
  const fieldCategories: Record<string, string> = {
    severity: 'Priority',
    employeeName: 'People',
    department: 'Organization',
    manager: 'Organization',
    weekEnding: 'Time Period',
    status: 'Status',
    expectedHours: 'Metrics',
    daysOverdue: 'Metrics',
    lastReminded: 'Activity',
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-10 w-72 mb-3" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <PageHeader
            title="Missing Timesheets Report"

            size={36}
            showActions={false}
            showBreadcrumb={false}
            icon={<FileWarning />}
          />
          <FeatureFeedbackCollector feature="Missing Timesheet" />
        </div>
        <Card className="border-error-200 bg-error-50 dark:bg-error-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-error-600" />
              <div>
                <p className="font-medium text-error-900 dark:text-error-100">Failed to load missing timesheets</p>
                <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Access denied — user's role does not have this report enabled
  if (!canViewReport('missingTimesheets')) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <PageHeader
            title="Missing Timesheets Report"

            size={36}
            showActions={false}
            showBreadcrumb={false}
            icon={<FileWarning />}
          />
        </div>
        <AccessRestricted message="You don't have permission to view the Missing Timesheets report. Contact your administrator to request access." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Missing Timesheets Report"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<FileWarning />}
        />
        <FeatureFeedbackCollector feature="Missing Timesheet" />
      </div>

      {/* Summary Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <NavigationMetricCardAlt
          title="Total Missing"
          value={metrics.totalMissing.toString()}
          icon={FileWarning}
          variant={
            metrics.totalMissing === 0 ? "success" :
            metrics.totalMissing > 10 ? "error" :
            "warning"
          }
          change={
            metrics.totalMissing === 0
              ? "All timesheets submitted"
              : `${metrics.uniqueEmployees} employee${metrics.uniqueEmployees !== 1 ? 's' : ''} · ${metrics.totalMissingChange <= 0 ? '' : '+'}${metrics.totalMissingChange} vs last period`
          }
        />

        <NavigationMetricCardAlt
          title="Compliance Rate"
          value={`${metrics.complianceRate}%`}
          icon={Target}
          variant={
            Number(metrics.complianceRate) >= 95 ? "success" :
            Number(metrics.complianceRate) >= 80 ? "warning" :
            "error"
          }
          change={`${metrics.complianceRateChange >= 0 ? '+' : ''}${Number(metrics.complianceRateChange).toFixed(1)}% vs last period`}
        />

        <NavigationMetricCardAlt
          title="Avg Days Overdue"
          value={metrics.avgDaysOverdue.toString()}
          icon={Clock}
          variant={
            Number(metrics.avgDaysOverdue) === 0 ? "success" :
            Number(metrics.avgDaysOverdue) <= 3 ? "warning" :
            "error"
          }
          change={`${metrics.avgDaysOverdueChange <= 0 ? '' : '+'}${Number(metrics.avgDaysOverdueChange).toFixed(1)} days vs last period`}
        />

        <NavigationMetricCardAlt
          title="Repeat Offenders"
          value={metrics.repeatOffenders.toString()}
          icon={Users}
          variant={
            metrics.repeatOffenders === 0 ? "success" :
            metrics.repeatOffenders <= 2 ? "warning" :
            "error"
          }
          change={
            metrics.repeatOffenders === 0
              ? "No critical overdue timesheets"
              : `11+ days overdue · ${metrics.repeatOffendersChange <= 0 ? '' : '+'}${metrics.repeatOffendersChange} vs last period`
          }
        />
      </div>

      {/* Missing Timesheets Table with EDS DataTableEnhanced */}
      <Card data-tour="reports-results-table">
        <CardHeader>
          <CardTitle>Missing Timesheets</CardTitle>
          <CardDescription>
            All employees with missing or incomplete timesheet submissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTableEnhanced
            columns={columns}
            data={missingTimesheets}
            searchKey="employeeName"
            searchPlaceholder="Search employees..."
            enableGrouping={true}
            enableColumnVisibility={true}
            enableSorting={true}
            enableFiltering={true}
            enableRowSelection={true}
            onSelectionChange={setSelectedEmployees}
            clearSelection={clearSelection}
            pageSize={20}
            storageKey="missing-timesheets-report"
            fieldLabels={fieldLabels}
            fieldCategories={fieldCategories}
            showSortIconsWhenNotSorted={false}
            toolbarActions={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendReminders}
                  className="gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  {selectedEmployees.length > 0 
                    ? `Send Reminders (${selectedEmployees.length})`
                    : 'Send Reminders (All)'}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={handleExport}
                  disabled={!canExportReport('missingTimesheets')}
                  title={!canExportReport('missingTimesheets') ? "You don't have permission to export this report" : "Export to Excel"}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};
